import React from 'react';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import { LaoLetter } from '../data/letters';
import { LaoNumber } from '../data/numbers';

interface SuccessScreenProps {
  item: LaoLetter | LaoNumber;
  onNext: () => void;
  onReplay: () => void;
}

const starDelay = [0.3, 0.6, 0.9];

const SuccessScreen: React.FC<SuccessScreenProps> = ({ item, onNext, onReplay }) => {
  const isLetter = 'char' in item;
  const char = isLetter ? (item as LaoLetter).char : (item as LaoNumber).laoChar;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-b from-yellow-200 via-pink-100 to-purple-200 flex flex-col items-center justify-center p-6 relative overflow-hidden"
    >
      <Confetti
        width={window.innerWidth}
        height={window.innerHeight}
        recycle={false}
        numberOfPieces={300}
      />

      {/* Big emoji */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 100, delay: 0.2 }}
        className="text-9xl mb-4"
      >
        {item.emoji}
      </motion.div>

      {/* Character + name */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-center mb-6"
      >
        <div className="text-6xl md:text-7xl font-bold text-purple-600">{char}</div>
        <div className="text-2xl md:text-3xl text-gray-600 mt-2">{item.name}</div>
      </motion.div>

      {/* 3 Stars */}
      <div className="flex gap-4 mb-8">
        {[0, 1, 2].map(i => (
          <motion.span
            key={i}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: starDelay[i], type: 'spring', stiffness: 200 }}
            className="text-6xl"
          >
            ⭐
          </motion.span>
        ))}
      </div>

      {/* Celebration text */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.2, type: 'spring' }}
        className="text-4xl font-bold text-pink-500 mb-8"
      >
        🎉 ເກັ່ງຫຼາຍ! 🎉
      </motion.div>

      {/* Buttons */}
      <div className="flex gap-4">
        <motion.button
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onNext}
          className="bg-gradient-to-r from-green-400 to-teal-400 text-white rounded-2xl px-8 py-4 text-2xl font-bold shadow-lg min-h-[48px]"
        >
          ຄຳຕໍ່ໄປ →
        </motion.button>
        <motion.button
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onReplay}
          className="bg-gradient-to-r from-orange-400 to-pink-400 text-white rounded-2xl px-8 py-4 text-2xl font-bold shadow-lg min-h-[48px]"
        >
          ຫຼີ້ນໃໝ່ 🔄
        </motion.button>
      </div>
    </motion.div>
  );
};

export default SuccessScreen;
